import React, { useState } from "react";
import { Trans, withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from "@mzc-pdc/ui";
import { EllipsisTypography } from "@components_v2/ellipsis-tooltip";
import Typography from "@components_v2/typography/typography";
import { IconAvata, IconImpossible } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import TooltipUserInfo from "../tooltip/userInfo";
import ScrollArea from "../ScrollArea";
import DialogWarningConfirm from "@components_v2/dialog-warning-confirm";
import { revokeExternalShareRecipient } from "@modules/propertyPanel";
import { setNotification } from "@modules/notification";

const RECIPIENT_TYPE_USER = "USER";
const COUNT_HEADER_KEY = "Downloads";
const COUNT_COLUMN_WIDTH = 108;
const ACTION_COLUMN_WIDTH = 68;

/**
 * 외부 공유 상세의 「공유 된」 탭. 수신자별 다운로드 횟수를 보여주고 다운로드 링크를 개별 회수한다.
 *
 * 회수 대상은 `type === "USER"` 수신자뿐이다. `GROUP` 은 권한을 회수해도 내려받기가
 * 막히지 않고 `downloads` 가 항상 0 이므로 횟수·회수 액션·회수 상태를 모두 비워 둔다.
 * `GROUP` 은 이메일 자체가 응답에 담기지 않으므로 이메일 줄도 그리지 않는다.
 *
 * 헤더·이름·이메일 위계와 아바타 색은 `routes/admins/users/list-users-v2.js` 의 사람 목록을 따르고,
 * 352px 패널에 맞춰 셀은 `size="small"`, 아바타는 18px 로 줄였다.
 * 열 폭은 ko·en·ja·pt-BR·zh 헤더와 회수 상태 문구의 렌더 폭으로 정했다. ja 「取り消し済み」만 열보다 길어
 * 회수 상태 문구는 줄바꿈을 허용한다.
 *
 * 회수에 실패하면 다이얼로그를 열어 둬 다시 시도할 수 있게 하고, 실패 안내는 axios 인터셉터에 맡긴다.
 *
 * @param {Object} item 다운로드 링크 상세. `id`·`sharedTo`·`maxDownloads`·`resourceSummary` 를 읽는다.
 * @param {boolean} isRevokable 다운로드 횟수·회수 열 노출 여부(stage 버전 게이트)
 */
const SharedWithView = (props) => {
    const { t, item, isRevokable } = props;
    const dispatch = useDispatch();

    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revokePending, setRevokePending] = useState(false);

    const onConfirmRevoke = async () => {
        setRevokePending(true);
        try {
            await dispatch(revokeExternalShareRecipient({ linkId: item.id, sharedToId: revokeTarget.id }));
            dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::Download link revoked.`, `Download link revoked.`),
                }),
            );
            setRevokeTarget(null);
        } catch (e) {
            // 실패 토스트는 axios 인터셉터가 띄운다.
        } finally {
            setRevokePending(false);
        }
    };

    const renderDownloads = (recipient) => {
        if (recipient.type !== RECIPIENT_TYPE_USER) return null;
        if (recipient.downloads == null) return null;

        return (
            <>
                <Typography
                    variant={"body1_400"}
                    component={"span"}
                    color={recipient.isRevoked ? theme.palette.greySecondary[600] : theme.palette.primary.main}
                >
                    {recipient.downloads}
                </Typography>
                <Typography variant={"body1_400"} component={"span"} color={theme.palette.greySecondary[600]}>
                    {` / ${item?.maxDownloads ?? "-"}`}
                </Typography>
            </>
        );
    };

    const renderRecipient = (recipient) => {
        const nameColor = recipient.isRevoked ? theme.palette.greySecondary[600] : theme.palette.greySecondary.main;

        if (recipient.type !== RECIPIENT_TYPE_USER) {
            return (
                <EllipsisTypography noWrap variant={"body1_500"} color={nameColor} sx={{ minWidth: 0 }}>
                    {recipient.name || "-"}
                </EllipsisTypography>
            );
        }

        if (!recipient.externalId) {
            return (
                <EllipsisTypography
                    noWrap
                    variant={"body3_400"}
                    color={theme.palette.greySecondary[600]}
                    sx={{ minWidth: 0 }}
                >
                    {recipient.email || "-"}
                </EllipsisTypography>
            );
        }

        return (
            <>
                <EllipsisTypography noWrap variant={"body1_500"} color={nameColor} sx={{ minWidth: 0 }}>
                    {recipient.name || "-"}
                </EllipsisTypography>
                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                    <EllipsisTypography
                        noWrap
                        variant={"body3_400"}
                        color={theme.palette.greySecondary[600]}
                        sx={{ minWidth: 0 }}
                    >
                        {recipient.email || "-"}
                    </EllipsisTypography>
                    <TooltipUserInfo target={recipient} id={recipient.externalId} />
                </div>
            </>
        );
    };

    const renderAction = (recipient) => {
        if (recipient.type !== RECIPIENT_TYPE_USER) return null;

        if (recipient.isRevoked) {
            return (
                <Typography variant={"body3_400"} color={theme.palette.greySecondary[600]}>
                    {t(`common::label::${"Revoked"}`, `Revoked`)}
                </Typography>
            );
        }

        return (
            <Tooltip title={t(`common::label::${"Revoke link"}`, `Revoke link`)}>
                <IconButton
                    size={"small"}
                    aria-label={t(`common::label::${"Revoke link"}`, `Revoke link`)}
                    onClick={() => setRevokeTarget(recipient)}
                >
                    <IconImpossible size={16} htmlColor={theme.palette.greySecondary[500]} />
                </IconButton>
            </Tooltip>
        );
    };

    return (
        <React.Fragment>
            <div className="control">
                <div className="justify-content-end">
                    <div>
                        {t(`common::label::${"Total"}`)}{" "}
                        <strong className="text-primary">{item?.recipientSummary.totalCount}</strong>
                    </div>
                </div>
            </div>
            <ScrollArea>
                <Table sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell size={"small"} sx={{ pl: 0 }}>
                                {t(`common::label::${"User"}`)}
                            </TableCell>
                            {isRevokable && (
                                <>
                                    <TableCell
                                        size={"small"}
                                        align={"right"}
                                        sx={{ width: COUNT_COLUMN_WIDTH, whiteSpace: "nowrap" }}
                                    >
                                        {t(`common::label::${COUNT_HEADER_KEY}`)}
                                    </TableCell>
                                    <TableCell
                                        size={"small"}
                                        align={"right"}
                                        sx={{ width: ACTION_COLUMN_WIDTH, whiteSpace: "nowrap", pr: "0 !important" }}
                                    >
                                        {t(`common::label::${"Action"}`)}
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {item?.sharedTo?.map((recipient) => (
                            <TableRow key={recipient.id}>
                                <TableCell size={"small"} sx={{ pl: 0 }}>
                                    <div className="d-flex align-items-center" style={{ gap: 8, minWidth: 0 }}>
                                        <IconAvata size={18} htmlColor={theme.palette.greySecondary[500]} />
                                        <div style={{ minWidth: 0 }}>{renderRecipient(recipient)}</div>
                                    </div>
                                </TableCell>
                                {isRevokable && (
                                    <>
                                        <TableCell
                                            size={"small"}
                                            align={"right"}
                                            sx={{ width: COUNT_COLUMN_WIDTH, whiteSpace: "nowrap" }}
                                        >
                                            {renderDownloads(recipient)}
                                        </TableCell>
                                        <TableCell
                                            size={"small"}
                                            align={"right"}
                                            sx={{ width: ACTION_COLUMN_WIDTH, pr: "0 !important" }}
                                        >
                                            {renderAction(recipient)}
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </ScrollArea>
            {revokeTarget && (
                <DialogWarningConfirm
                    open
                    title={t(`common::msg::Revoke this download link?`, `Revoke this download link?`)}
                    message={
                        <>
                            <Trans
                                i18nKey={`common::msg::'{{subject}}' shared with {{sharedWith}} will no longer be downloadable.`}
                                defaults={`'{{subject}}' shared with {{sharedWith}} will no longer be downloadable.`}
                                values={{
                                    subject: item?.resourceSummary?.first?.name,
                                    sharedWith: revokeTarget.name
                                        ? `${revokeTarget.name}(${revokeTarget.email})`
                                        : revokeTarget.email,
                                }}
                            />
                            {"\n"}
                            {t(
                                `common::msg::A revoked link cannot be restored. Share again to send a new link.`,
                                `A revoked link cannot be restored. Share again to send a new link.`,
                            )}
                        </>
                    }
                    confirmLabel={t(`common::label::Revoke`, `Revoke`)}
                    confirmColor="error"
                    loading={revokePending}
                    onConfirm={onConfirmRevoke}
                    onClose={() => !revokePending && setRevokeTarget(null)}
                />
            )}
        </React.Fragment>
    );
};
export default withTranslation()(SharedWithView);
